/**
 * OceanHero — a search extension that funds ocean plastic removal
 *
 * Its single web-accessible-resource group exposes /img/*, and all root-level .svg and
 * .webp files, but scopes them to Google Search on every regional Google domain
 * (www.google.com, www.google.co.uk, and hundreds more) rather than to every site. No
 * `use_dynamic_url`, so the files are static — but reachable only from a Google origin.
 * The root-level .svg / .webp files carry rotating build hashes, so we probe the stable,
 * vendor-named sprites under /img/ instead.
 *
 * So this extension IS detectable, but only from a Google Search page. From any other
 * origin a negative result proves nothing — a limitation of where the probe runs.
 *
 * Manifest verified against version 10.7.
 */

import { probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'ijpgjiobapjdadigoagamcmhmdfdggmg';

export const oceanHero: ExtensionDefinition = {
  id: ID,
  name: 'OceanHero: Save Our Oceans by Surfing the Web',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 600_000,
  requiredOrigin: 'https://www.google.com',
  notes:
    'WAR scoped to Google Search on every regional Google domain, not <all_urls>, so ' +
    'only a Google page can detect it. Root .svg/.webp are build-hashed; the /img/ ' +
    'sprites are vendor-named and stable.',
  signals: [
    {
      id: 'war-bottle-icon',
      kind: 'web-accessible-resource',
      description:
        'Fetch img/bottle-icon.svg — a vendor-named sprite, static but only exposed to ' +
        'Google Search origins.',
      weight: 1,
      run: () => probeFetch(ID, 'img/bottle-icon.svg'),
    },
    {
      id: 'war-logo-whale',
      kind: 'web-accessible-resource',
      description:
        'Fetch img/logo-whale.webp — a second vendor-named sprite, corroborating the ' +
        'identification.',
      weight: 0.5,
      run: () => probeFetch(ID, 'img/logo-whale.webp'),
    },
    {
      id: 'war-bottle-counter',
      kind: 'web-accessible-resource',
      description:
        'Fetch img/bottle-counter.webp — a third vendor-named sprite, independent of the ' +
        'other two.',
      weight: 0.5,
      run: () => probeFetch(ID, 'img/bottle-counter.webp'),
    },
  ],
};
